import type { ReactElement, FormEvent, ChangeEvent } from "react";

import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios, { AxiosError } from "axios";
import { Lock, Eye, EyeOff, CheckCircle } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * API error response
 */
interface ApiErrorResponse {
  detail?: string;
}

/**
 * ResetPasswordPage Component
 *
 * Page for setting a new password after requesting a reset.
 */
const ResetPasswordPage = (): ReactElement => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [token, setToken] = useState<string>("");

  useEffect(() => {
    // Get token from URL or localStorage (dev mode)
    const urlToken = searchParams.get("token");
    const storedToken = localStorage.getItem("reset_token");

    if (urlToken) {
      setToken(urlToken);
    } else if (storedToken) {
      setToken(storedToken);
      localStorage.removeItem("reset_token");
    } else {
      toast.error("Invalid reset link");
      navigate("/login");
    }
  }, [searchParams, navigate]);

  const handleReset = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    if (!password || !confirmPassword) {
      toast.error("Please fill in all fields");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/auth/reset-password`,
        {
          token,
          new_password: password,
        },
        { withCredentials: true }
      );

      setSuccess(true);
      toast.success("Password reset successfully!");

      // Redirect after 2 seconds
      setTimeout(() => {
        navigate("/home");
      }, 2000);
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      toast.error(
        axiosError.response?.data?.detail || "Failed to reset password"
      );
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{
          background:
            "linear-gradient(to bottom, var(--st-bg-page) 0%, var(--st-border-light) 50%, var(--st-border-medium) 100%)",
        }}
      >
        <div className="card-warm text-center animate-slide-up">
          <div
            className="inline-flex items-center justify-center w-20 h-20 mb-6 rounded-full"
            style={{
              background: "linear-gradient(135deg, #4CAF50 0%, #45a049 100%)",
            }}
          >
            <CheckCircle className="w-10 h-10 text-white" />
          </div>
          <h1
            className="text-3xl font-bold mb-3"
            style={{ fontFamily: "Chewy, cursive", color: "var(--st-text-heading)" }}
          >
            Password Reset!
          </h1>
          <p
            className="mb-4"
            style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
          >
            Redirecting you to home...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{
        background:
          "linear-gradient(to bottom, var(--st-bg-page) 0%, var(--st-border-light) 50%, var(--st-border-medium) 100%)",
      }}
    >
      <div className="container mx-auto px-4 max-w-md">
        <div className="card-warm animate-slide-up">
          <div className="text-center mb-6">
            <h1
              className="text-3xl font-bold mb-2"
              style={{ fontFamily: "Chewy, cursive", color: "var(--st-text-heading)" }}
            >
              Set New Password
            </h1>
            <p
              style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
            >
              Enter your new password below
            </p>
          </div>

          <form onSubmit={handleReset} className="space-y-4">
            <div>
              <label
                className="block text-sm font-semibold mb-2"
                style={{
                  fontFamily: "Quicksand, sans-serif",
                  color: "var(--st-text-heading)",
                }}
              >
                New Password
              </label>
              <div className="relative">
                <Lock
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5"
                  style={{ color: "var(--st-text-muted)" }}
                />
                <input
                  type={showPassword ? "text" : "password"}
                  data-testid="new-password-input"
                  value={password}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setPassword(e.target.value)
                  }
                  placeholder="Min 6 characters"
                  className="w-full pl-11 pr-11 py-3 rounded-xl border-2 focus:outline-none focus:border-orange-300"
                  style={{
                    fontFamily: "Quicksand, sans-serif",
                    borderColor: "var(--st-border-medium)",
                    background: "var(--st-bg-surface)",
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" style={{ color: "var(--st-text-muted)" }} />
                  ) : (
                    <Eye className="w-5 h-5" style={{ color: "var(--st-text-muted)" }} />
                  )}
                </button>
              </div>
            </div>

            <div>
              <label
                className="block text-sm font-semibold mb-2"
                style={{
                  fontFamily: "Quicksand, sans-serif",
                  color: "var(--st-text-heading)",
                }}
              >
                Confirm Password
              </label>
              <div className="relative">
                <Lock
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5"
                  style={{ color: "var(--st-text-muted)" }}
                />
                <input
                  type={showPassword ? "text" : "password"}
                  data-testid="confirm-new-password-input"
                  value={confirmPassword}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setConfirmPassword(e.target.value)
                  }
                  placeholder="Confirm new password"
                  className="w-full pl-11 pr-4 py-3 rounded-xl border-2 focus:outline-none focus:border-orange-300"
                  style={{
                    fontFamily: "Quicksand, sans-serif",
                    borderColor: "var(--st-border-medium)",
                    background: "var(--st-bg-surface)",
                  }}
                />
              </div>
            </div>

            <button
              type="submit"
              data-testid="reset-password-btn"
              disabled={loading}
              className="w-full py-3 rounded-xl font-bold transition-all disabled:opacity-50"
              style={{
                background:
                  "linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)",
                color: "white",
                fontFamily: "Chewy, cursive",
                fontSize: "1.1rem"
              }}
            >
              {loading ? "Resetting..." : "Reset Password"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
